import React, { useState, useMemo, useContext, useEffect, useRef } from "react";
import { Flex, Modal } from "antd";
import dayjs from "dayjs";
import { AppContext } from "../../../context/AppContext";
import { useListViewStore } from "../../../pages/listViewStore";
import { DEFAULT_CONTACT_ID } from "../../../pages/ConversationContext";
import { default as HSButton } from "../../custom/input/button/Button";
import { INBOX_DEFAULT_FILTERS } from "../../../constants/AppConstants";
import {
    OSYNC_FILTER_FIELDS,
    PHONE_CHANNEL_PREFIX,
    TEAM_CHANNEL_PREFIX,
    CHANNEL_FILTER_TYPE,
    buildPlaceholderMap,
    parseFilterDefinitionToTempValues,
} from "../FilterComponents/config/filterFieldConfig";
import { getOptionsForField, buildFilterDefinitionForSave } from "../FilterComponents/config/filterFieldOptions";
import { renderFilterFieldByUiComponent } from "../FilterComponents/InboxFilterFieldRenderers";
import "./InboxFilterPopoverCard.css";
import { useSavedFilters } from "../FilterComponents/hooks/useSavedFilters";
import { USER_VISIBILITY_SCOPE } from "../FilterComponents/enum/filterEnums";
import { ERROR_MESSAGES } from "../../../constants/messages/en_US";
import { useNavigate, useLocation } from "react-router-dom";
import HInput from "../../custom/input/input/Input";
import HRadioGroup from "../../custom/input/radio/HRadioGroup";
import { useTeamStore } from "../../../stores/teamStore";
import useHttp from "../../../hooks/useHttp";
import { useRolesContext } from "../../../context/RolesContext";
import { checkUserHasModuleAccess } from "../../../pages/utils/commonUtils";
const INBOX_FILTER_FIELDS = OSYNC_FILTER_FIELDS.filter(
    (f) => f.visible && f.module === "contacts"
);

/** Returns the active channel or team selection based on channelFilterType. */
const getActiveRightServiceId = (values, filterType) =>
    filterType === CHANNEL_FILTER_TYPE.CHANNEL
        ? (values?.rightServiceIdChannels ?? [])
        : (values?.rightServiceIdTeams ?? []);

const getChannelFilterTypeFromValues = (values) => {
    if (Array.isArray(values) && values.some((v) => String(v).includes(PHONE_CHANNEL_PREFIX))) {
        return CHANNEL_FILTER_TYPE.CHANNEL;
    }
    if (Array.isArray(values) && values.some((v) => String(v).includes(TEAM_CHANNEL_PREFIX))) {
        return CHANNEL_FILTER_TYPE.TEAM;
    }
    if (Array.isArray(values) && values.length > 0) {
        return CHANNEL_FILTER_TYPE.CHANNEL;
    }
    return CHANNEL_FILTER_TYPE.CHANNEL;
};

const getInitialTempFilterValues = (storeValues) => {
    const base = INBOX_FILTER_FIELDS.reduce((acc, field) => {
        const raw = field.storeKey ? storeValues[field.storeKey] : undefined;
        const defaultVal = field.defaultValue;
        if (field.fieldName === "readStatus") acc[field.fieldName] = raw ?? defaultVal;
        else if (field.fieldName === "createdTime") acc[field.fieldName] = raw ?? defaultVal;
        else if (field.fieldName === "rightServiceId" && Array.isArray(raw)) {
            const channelType = getChannelFilterTypeFromValues(raw);
            const normalizedRaw = raw.map((v) =>
                typeof v === "string" && v.startsWith(TEAM_CHANNEL_PREFIX)
                    ? v.slice(TEAM_CHANNEL_PREFIX.length)
                    : v
            );
            acc.rightServiceIdChannels = channelType === CHANNEL_FILTER_TYPE.CHANNEL ? normalizedRaw : [];
            acc.rightServiceIdTeams = channelType === CHANNEL_FILTER_TYPE.TEAM ? normalizedRaw : [];
        } else if (Array.isArray(defaultVal)) acc[field.fieldName] = Array.isArray(raw) ? raw : defaultVal;
        else acc[field.fieldName] = raw ?? defaultVal;
        return acc;
    }, {});
    if (!("rightServiceIdChannels" in base)) base.rightServiceIdChannels = [];
    if (!("rightServiceIdTeams" in base)) base.rightServiceIdTeams = [];
    return base;
};

const getDefaultTempFilterValues = () => {
    const base = INBOX_FILTER_FIELDS.reduce((acc, field) => {
        acc[field.fieldName] = field.defaultValue;
        return acc;
    }, {});
    base.rightServiceIdChannels = [];
    base.rightServiceIdTeams = [];
    return base;
};

/** Returns the set of channel/team IDs the user has access to (from services). */
const getAccessibleChannelIds = (servicesList, channelFilterType) => {
    if (!Array.isArray(servicesList)) return new Set();
    const ids = new Set();
    if (channelFilterType === CHANNEL_FILTER_TYPE.TEAM) {
        const teamsNode = servicesList.find((s) => s.value === "teams");
        (teamsNode?.children || []).forEach((t) => {
            if (t?.value) ids.add(t.value);
        });
    } else {
        servicesList
            .filter((s) => s.value !== "All" && s.value !== "teams")
            .forEach((service) => {
                if (service?.value) ids.add(service.value);
                (service?.children || []).forEach((child) => {
                    if (child?.value) ids.add(child.value);
                });
            });
    }
    return ids;
};

const InboxFilterPopoverCard = ({
    services,
    setIsChannelPresent,
    setSelectedContact,
    selectedContactId,
    setPopoverOpen,
    popoverOpen,
    editingFilter,
    activeFilter,
    onClearActiveFilter,
    onEditComplete,
    onFilterSaved,
    onFilterUpdated,
    onFilterCreated,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [appProperties] = useContext(AppContext);
    const store = useListViewStore();
    const { createFilter, updateFilter } = useSavedFilters();
    const [updateInProgress, setUpdateInProgress] = useState(false);
    const isEditMode = Boolean(editingFilter);
    const [visibility, setVisibility] = useState(USER_VISIBILITY_SCOPE.ONLY_ME);
    const [saveAsViewOpen, setSaveAsViewOpen] = useState(false);
    const [filterNameForSave, setFilterNameForSave] = useState("");
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const {
        activeTab,
        selectedChannels,
        selectedConversationStatus,
        selectedFlagFilter,
        selectedAssignees,
        selectedReadStatus,
        selectedDateRange,
    } = store;
    const { teams, currentUserTeams, currentUserTeamFetched, allTeamsFetched, fetchCurrentUserTeams, fetchTeams } =
        useTeamStore.getState();
    const { fetchData } = useHttp();
    const [rolesProperties] = useRolesContext();
    const userHasTeamsModuleAccess = useMemo(
        () => checkUserHasModuleAccess(rolesProperties?.currentRoleDetails, "teams"),
        [rolesProperties?.currentRoleDetails]
    );
    const teamOptions = useMemo(() => {
        const teamMap = new Map();
        const addTeamIfValid = (team) => {
            if (team.teamsEntity?.teamId) {
                teamMap.set(team.teamsEntity?.teamId, team?.teamsEntity);
            } else {
                teamMap.set(team.teamId, team);
            }
        };
        (userHasTeamsModuleAccess ? teams : []).forEach(addTeamIfValid);
        (currentUserTeams || []).forEach(addTeamIfValid);
        return Array.from(teamMap.values());
    }, [userHasTeamsModuleAccess, teams, currentUserTeams]);

    const tabFilterConfig = useMemo(() => {
        const config = {};
        for (const def of Object.values(INBOX_DEFAULT_FILTERS)) {
            const tabValue = def.value;
            const base = { disabledFilters: def.disabledFilters ?? [] };
            if (def.readStatus) base.readStatus = def.readStatus;
            if (def.needsCurrentUserAssignees && appProperties?.currentUser?.osyncUserId) {
                base.assignees = [appProperties.currentUser.osyncUserId];
            }
            config[tabValue] = config[tabValue] ? { ...config[tabValue], ...base } : base;
        }
        return config;
    }, [appProperties?.currentUser?.osyncUserId]);

    const tabConfig = tabFilterConfig[activeTab] ?? tabFilterConfig.all ?? { disabledFilters: [] };

    const [tempFilterValues, setTempFilterValues] = useState(() => getDefaultTempFilterValues());
    const [channelFilterType, setChannelFilterType] = useState(CHANNEL_FILTER_TYPE.CHANNEL);
    const initialEditingValuesRef = useRef(null);

    const assigneeOptions = useMemo(
        () => [
            ...(appProperties?.licenseObj?.allLicensedUserDetails?.map((user) => {
                const hasName = user?.firstName || user?.lastName;
                const name = hasName ? `${user?.firstName || ""} ${user?.lastName || ""}`.trim() : "";
                const email = user?.email || "";
                const displayName = hasName && email ? `${name} (${email})` : email || name;
                return {
                    ...user,
                    label: displayName,
                    labelText: displayName,
                    value: user.osyncUserId,
                    userData: user,
                    group: "Users",
                };
            }) || []),
            ...(teamOptions?.map((team) => {
                return {
                    ...team,
                    label: team.friendlyName,
                    labelText: team.friendlyName,
                    value: team.teamId,
                    teamData: team,
                    group: "Teams",
                };
            }) || []),
        ],
        [appProperties?.licenseObj?.allLicensedUserDetails, teamOptions]
    );

    useEffect(() => {
        if (!popoverOpen) return;
        if (!currentUserTeamFetched) {
            fetchCurrentUserTeams(fetchData, appProperties);
        }
        if (userHasTeamsModuleAccess && !allTeamsFetched) {
            fetchTeams(fetchData, appProperties);
        }
    }, [
        popoverOpen,
        userHasTeamsModuleAccess,
        currentUserTeamFetched,
        allTeamsFetched,
        fetchCurrentUserTeams,
        fetchTeams,
        fetchData,
        appProperties?.osyncId,
    ]);

    useEffect(() => {
        if (!popoverOpen) return;

        const filterToPrefill = editingFilter ?? activeFilter;

        if (filterToPrefill?.filterDefinition) {
            const defaults = getDefaultTempFilterValues();
            if (!currentUserTeamFetched) {
                fetchCurrentUserTeams(fetchData, appProperties);
            }
            const placeholderMap = buildPlaceholderMap(appProperties);
            const parsed = parseFilterDefinitionToTempValues(filterToPrefill.filterDefinition, placeholderMap);
            const merged = INBOX_FILTER_FIELDS.reduce((acc, field) => {
                acc[field.fieldName] = parsed[field.fieldName] ?? defaults[field.fieldName];
                return acc;
            }, {});
            const channelType = parsed.channelFilterType ?? CHANNEL_FILTER_TYPE.CHANNEL;
            const accessibleIds = getAccessibleChannelIds(services || [], channelType);
            const rawRightServiceId = Array.isArray(merged.rightServiceId) ? merged.rightServiceId : [];
            const filteredRightServiceId = rawRightServiceId.filter((id) => accessibleIds.has(id));
            merged.rightServiceIdChannels = channelType === CHANNEL_FILTER_TYPE.CHANNEL ? filteredRightServiceId : [];
            merged.rightServiceIdTeams = channelType === CHANNEL_FILTER_TYPE.TEAM ? filteredRightServiceId : [];
            setTempFilterValues(merged);
            setChannelFilterType(channelType);
            const isUserSavedFilter = editingFilter || (activeFilter && !activeFilter.systemDefined);
            if (isUserSavedFilter) {
                initialEditingValuesRef.current = { values: { ...merged }, channelType };
            } else {
                initialEditingValuesRef.current = null;
            }
        } else {
            initialEditingValuesRef.current = null;
            const effectiveReadStatus =
                activeTab === INBOX_DEFAULT_FILTERS.unread?.value && tabConfig?.readStatus
                    ? tabConfig.readStatus
                    : selectedReadStatus;
            const initialValues = getInitialTempFilterValues({
                selectedChannels,
                selectedConversationStatus,
                selectedFlagFilter,
                selectedAssignees,
                selectedReadStatus: effectiveReadStatus,
                selectedDateRange,
            });
            setTempFilterValues(initialValues);
            setChannelFilterType(getChannelFilterTypeFromValues(selectedChannels));
        }
    }, [
        popoverOpen,
        editingFilter,
        activeFilter,
        activeTab,
        tabConfig,
        appProperties,
        services,
        selectedChannels,
        selectedConversationStatus,
        selectedFlagFilter,
        selectedAssignees,
        selectedReadStatus,
        selectedDateRange,
        currentUserTeams,
        currentUserTeamFetched,
        fetchCurrentUserTeams,
        fetchData,
    ]);

    const setFieldValue = (fieldName, value) => {
        setTempFilterValues((prev) => ({ ...prev, [fieldName]: value }));
    };

    /** Returns tempFilterValues with rightServiceId derived from the active channel/team stash. */
    const getEffectiveTempFilterValues = () => ({
        ...tempFilterValues,
        rightServiceId: getActiveRightServiceId(tempFilterValues, channelFilterType),
    });

    const applyTempValuesToStore = () => {
        const effectiveValues = getEffectiveTempFilterValues();
        INBOX_FILTER_FIELDS.forEach((field) => {
            if (!field.storeKey) return;

            const setterName = "set" + field.storeKey.charAt(0).toUpperCase() + field.storeKey.slice(1);
            const setter = store[setterName];
            if (!setter) return;

            const tabDisabledKey = field.tabDisabledKey;
            let value =
                tabDisabledKey && tabConfig.disabledFilters.includes(tabDisabledKey)
                    ? tabConfig[tabDisabledKey]
                    : effectiveValues[field.fieldName];

            if (
                field.fieldName === "rightServiceId" &&
                channelFilterType === CHANNEL_FILTER_TYPE.TEAM &&
                Array.isArray(value) &&
                value.length > 0
            ) {
                value = value.map((v) =>
                    typeof v === "string" && !v.includes(PHONE_CHANNEL_PREFIX) && !v.includes(TEAM_CHANNEL_PREFIX)
                        ? TEAM_CHANNEL_PREFIX + v
                        : v
                );
            }

            // Sanitize string-array filter values to prevent null/undefined from causing charAt errors
            if (
                Array.isArray(value) &&
                (field.fieldName === "userFlagColorMap" || field.fieldName === "conversationStatusType")
            ) {
                value = value.filter((v) => v != null && typeof v === "string");
            }

            setter(value);
        });
        setIsChannelPresent(false);
        setSelectedContact(null);
        selectedContactId.current = DEFAULT_CONTACT_ID;
        navigate(`/inbox${location.search}`);
        setPopoverOpen(false);
    };

    const handleSaveChanges = () => {
        applyTempValuesToStore();
        onClearActiveFilter?.();
        if (isEditMode && onEditComplete) onEditComplete();
    };

    const handleApplyWithoutSaving = () => {
        applyTempValuesToStore();
        onClearActiveFilter?.();
        if (onEditComplete) onEditComplete();
    };

    const hasAnyFilterCondition = () => {
        const filterDefinition = buildFilterDefinitionForSave(getEffectiveTempFilterValues(), { channelFilterType });
        return filterDefinition?.conditions?.length > 0;
    };

    const filterToUpdate = editingFilter ?? (activeFilter && !activeFilter.systemDefined ? activeFilter : null);

    const handleUpdateFilter = async () => {
        if (!filterToUpdate?.filterId) return;
        if (!hasAnyFilterCondition()) {
            setSaveError("Select at least one filter condition");
            return;
        }
        setUpdateInProgress(true);
        setSaveError(null);
        try {
            const filterDefinition = buildFilterDefinitionForSave(getEffectiveTempFilterValues(), { channelFilterType });
            await updateFilter(filterToUpdate.filterId, {
                filterDefinition,
                filterName: filterToUpdate.filterName,
                visibility: filterToUpdate.visibility,
            });
            applyTempValuesToStore();
            onFilterSaved?.();
            onFilterUpdated?.(filterToUpdate.filterId, filterDefinition);
            if (onEditComplete) onEditComplete();
        } catch (err) {
            setSaveError(err?.message ?? "Failed to update view");
        } finally {
            setUpdateInProgress(false);
        }
    };

    const handleReset = () => {
        setTempFilterValues(getDefaultTempFilterValues());
        setChannelFilterType(CHANNEL_FILTER_TYPE.CHANNEL);
    };

    const openSaveAsView = () => {
        setFilterNameForSave("");
        setSaveError(null);
        setVisibility(USER_VISIBILITY_SCOPE.ONLY_ME);
        setSaveAsViewOpen(true);
    };

    const closeSaveAsView = () => {
        if (!saveInProgress) {
            setSaveAsViewOpen(false);
            setFilterNameForSave("");
            setSaveError(null);
        }
    };

    const handleSaveAsView = async () => {
        const name = filterNameForSave?.trim();
        if (!name) {
            setSaveError(ERROR_MESSAGES.VALIDATION.SAVED_VIEW_NAME_REQUIRED);
            return;
        }
        if (!hasAnyFilterCondition()) {
            setSaveError("Select at least one filter condition");
            return;
        }
        setSaveInProgress(true);
        setSaveError(null);
        try {
            const filterDefinition = buildFilterDefinitionForSave(getEffectiveTempFilterValues(), { channelFilterType });
            const payload = {
                filterName: name,
                filterDefinition,
                visibility: visibility,
            };
            const createdFilter = await createFilter(payload);
            closeSaveAsView();
            setPopoverOpen(false);
            onFilterSaved?.();
            onFilterCreated?.(createdFilter);
            if (isEditMode && onEditComplete) onEditComplete();
        } catch (err) {
            setSaveError(err?.message ?? "Failed to save view");
        } finally {
            setSaveInProgress(false);
        }
    };

    const optionsContext = useMemo(
        () => ({
            services: services || [],
            assigneeOptions,
            dayjs,
            channelFilterType,
            onChannelFilterTypeChange: (nextType) => setChannelFilterType(nextType),
        }),
        [services, assigneeOptions, channelFilterType]
    );

    const hasSavedFilterContext = Boolean(
        editingFilter || (activeFilter && !activeFilter.systemDefined)
    );

    const hasEditedFilter = useMemo(() => {
        if (!hasSavedFilterContext || !initialEditingValuesRef.current) return false;
        const initial = initialEditingValuesRef.current;
        const currentEffectiveValues = {
            ...tempFilterValues,
            rightServiceId: getActiveRightServiceId(tempFilterValues, channelFilterType),
        };
        const initialRightServiceId = getActiveRightServiceId(initial.values, initial.channelType);
        const initialEffectiveValues = {
            ...initial.values,
            rightServiceId:
                initialRightServiceId.length > 0
                    ? initialRightServiceId
                    : (initial.values.rightServiceId ?? []),
        };
        const currentDef = buildFilterDefinitionForSave(currentEffectiveValues, { channelFilterType });
        const initialDef = buildFilterDefinitionForSave(initialEffectiveValues, { channelFilterType: initial.channelType });
        const currentStr = JSON.stringify(currentDef?.conditions ?? []);
        const initialStr = JSON.stringify(initialDef?.conditions ?? []);
        return currentStr !== initialStr;
    }, [hasSavedFilterContext, tempFilterValues, channelFilterType]);

    const showEditModeButtons = hasSavedFilterContext && hasEditedFilter;
    const showDefaultButtons = !hasSavedFilterContext;

    return (
        <Flex vertical gap="middle">
            {INBOX_FILTER_FIELDS.map((field) => {
                if (field.tabDisabledKey && tabConfig.disabledFilters.includes(field.tabDisabledKey)) {
                    return null;
                }
                const isRightServiceIdField = field.fieldName === "rightServiceId";
                const value = isRightServiceIdField
                    ? getActiveRightServiceId(tempFilterValues, channelFilterType)
                    : tempFilterValues[field.fieldName];
                const onChange = isRightServiceIdField
                    ? (v) =>
                          setTempFilterValues((prev) => ({
                              ...prev,
                              [channelFilterType === CHANNEL_FILTER_TYPE.CHANNEL ? "rightServiceIdChannels" : "rightServiceIdTeams"]:
                                  Array.isArray(v) ? v : [],
                          }))
                    : (v) => setFieldValue(field.fieldName, v);
                const options = getOptionsForField(field, optionsContext);
                return (
                    <React.Fragment key={field.fieldId}>
                        {renderFilterFieldByUiComponent({
                            field,
                            value,
                            onChange,
                            options,
                            context: optionsContext,
                        })}
                    </React.Fragment>
                );
            })}
            {showEditModeButtons && (
                <>
                    <div className="d-flex justify-content-between align-items-center ms-1">
                        <span
                            data-cy="inbox-filter-apply-without-saving"
                            className="hs-link"
                            onClick={handleApplyWithoutSaving}
                        >
                            Apply without saving
                        </span>
                        <span
                            data-cy="inbox-filter-save-as-new-view"
                            className="hs-link"
                            onClick={openSaveAsView}
                        >
                            + Save as new view
                        </span>
                    </div>
                    <div className="ms-1">
                        <HSButton
                            data-cy="inbox-filter-update-button"
                            size="s"
                            variant="warning"
                            buttonClassName="w-100"
                            type=""
                            onClick={handleUpdateFilter}
                            loading={updateInProgress}
                        >
                           <div className="hs-color-white">Update</div>
                        </HSButton>
                    </div>
                </>
            )}
            {showDefaultButtons && (
                <>
                    <div className="d-flex justify-content-between align-items-center ms-1">
                        <div data-cy="inbox-filter-reset-button" className="hs-link" onClick={handleReset}>
                            Reset Filters
                        </div>
                        <span data-cy="inbox-filter-save-as-view" className="hs-link" onClick={openSaveAsView}>
                            Save as view
                        </span>
                    </div>
                    <div className="ms-1">
                        <HSButton
                            data-cy="inbox-filter-apply-button"
                            size="s"
                            type="primary"
                            onClick={handleSaveChanges}
                            buttonClassName="w-100"
                        >
                            Apply Filters
                        </HSButton>
                    </div>
                </>
            )}
            {saveError && <div className="text-danger mt-1 small ms-1">{saveError}</div>}
            <Modal
                title="Save as view"
                open={saveAsViewOpen}
                onCancel={closeSaveAsView}
                onOk={handleSaveAsView}
                confirmLoading={saveInProgress}
                okText="Save"
                destroyOnClose
                footer={
                    <div className="d-flex justify-content-end gap-2">
                        <HSButton variant="cancel" onClick={closeSaveAsView}>
                            Cancel
                        </HSButton>
                        <HSButton type="primary" onClick={handleSaveAsView} loading={saveInProgress}>
                            Save
                        </HSButton>
                    </div>
                }
            >
                <div className="d-flex flex-column gap-3 mt-4">
                    <HInput
                        formItemName="filterNameForSave"
                        formItemLabel="Filter Name"
                        required
                        errorMessage="View name is required"
                        placeholder="e.g., Urgent Support Tickets"
                        validator={(_, value) => {
                            if (!value) {
                                return Promise.reject(new Error("Filter name is required"));
                            }
                            return Promise.resolve();
                        }}
                        value={filterNameForSave}
                        onChange={(e) => setFilterNameForSave(e.target.value)}
                        onPressEnter={handleSaveAsView}
                        status={saveError ? "error" : undefined}
                    />
                    <div>
                        <HRadioGroup
                            options={[
                                { label: "All users", value: USER_VISIBILITY_SCOPE.ORG },
                                { label: "Only me", value: USER_VISIBILITY_SCOPE.ONLY_ME },
                            ]}
                            radioGroupProps={{ value: visibility, onChange: (e) => setVisibility(e.target.value) }}
                            groupLabel="Visibility"
                            className="mt-2"
                        />
                    </div>
                </div>
                {saveError && <div className="text-danger mt-1 small">{saveError}</div>}
            </Modal>
        </Flex>
    );
};

export default InboxFilterPopoverCard;
